import React, { useState, useMemo, useEffect } from 'react';
import { supabase } from '../supabase';
import { QuestionCard } from './QuestionCard';
import { BookOpenIcon, ChevronRightIcon, ArrowLeftIcon } from './icons/InterfaceIcons';

const mathematicsChapters = [
    { name: 'Application of Derivatives', count: 127 },
    { name: 'Area Under Curves', count: 45 },
    { name: 'Basic of Mathematics', count: 58 },
    { name: 'Binary Numbers', count: 31 },
    { name: 'Binomial Theorem', count: 73 },
    { name: 'Circle', count: 50 },
    { name: 'Complex Number', count: 122 },
    { name: 'Continuity and Differentiability', count: 83 },
    { name: 'Definite Integration', count: 103 },
    { name: 'Determinants', count: 132 },
    { name: 'Differential Equations', count: 121 },
    { name: 'Differentiation', count: 93 },
    { name: 'Ellipse', count: 35 },
    { name: 'Functions', count: 113 },
    { name: 'Heights and Distances', count: 45 },
    { name: 'Hyperbola', count: 11 },
    { name: 'Indefinite Integration', count: 71 },
    { name: 'Inverse Trigonometric Functions', count: 60 },
    { name: 'Limits', count: 71 },
    { name: 'Mathematical Induction', count: 2 },
    { name: 'Matrices', count: 139 },
    { name: 'Pair of Lines', count: 1 },
    { name: 'Parabola', count: 17 },
    { name: 'Permutation Combination', count: 111 },
    { name: 'Probability', count: 253 },
    { name: 'Properties of Triangles', count: 57 },
    { name: 'Quadratic Equation', count: 144 },
    { name: 'Sequences and Series', count: 167 },
    { name: 'Sets and Relations', count: 142 },
    { name: 'Statistics', count: 218 },
    { name: 'Straight Lines', count: 179 },
    { name: 'Three Dimensional Geometry', count: 160 },
    { name: 'Trigonometric Equations', count: 18 },
    { name: 'Trigonometric Ratios & Identities', count: 210 },
];

const generalAbilityChapters = [
    { name: 'English', count: 1378 },
    { name: 'General Science', count: 1432 },
    { name: 'General Studies', count: 1390 },
];

const PAGE_SIZE = 10;

const getDifficultyFromLevel = (level) => {
    switch (level) {
        case 1: return 'Easy';
        case 2: return 'Medium';
        case 3: return 'Hard';
        default: return 'Medium';
    }
};

const ChapterCard: React.FC<{ chapter: { name: string; count: number; }, onClick: () => void }> = ({ chapter, onClick }) => (
    <div onClick={onClick} className="bg-brand-surface border border-brand-border rounded-lg p-4 flex items-center justify-between hover:bg-brand-primary/5 hover:border-brand-primary transition-all duration-200 cursor-pointer group">
        <div className="flex items-center space-x-4">
            <BookOpenIcon className="w-5 h-5 text-brand-text-secondary group-hover:text-brand-primary transition-colors" />
            <div>
                <h3 className="font-semibold text-brand-text-primary">{chapter.name}</h3>
                <p className="text-sm text-brand-text-secondary">{chapter.count} Questions</p>
            </div>
        </div>
        <ChevronRightIcon className="w-5 h-5 text-brand-text-secondary group-hover:text-brand-primary transition-colors" />
    </div>
);

export const NDAChapterWiseView = ({ user, onToggleBookmark, onNavigateToSignIn }) => {
    const [activeSubject, setActiveSubject] = useState('Mathematics');
    const [selectedChapter, setSelectedChapter] = useState<string | null>(null);
    const [questions, setQuestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeDifficulty, setActiveDifficulty] = useState('All');
    const [currentPage, setCurrentPage] = useState(1);

     useEffect(() => {
        if (!selectedChapter) {
            setQuestions([]);
            return;
        }

        const fetchQuestions = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const { data, error: dbError } = await supabase
                    .from('nda')
                    .select('*')
                    .eq('subject', activeSubject)
                    .eq('tags', selectedChapter);

                if (dbError) throw dbError;

                if (data) {
                    const formattedQuestions = data.map((q) => ({
                        id: q.id.toString(),
                        text: q.question,
                        options: [q.option1, q.option2, q.option3, q.option4].filter(opt => opt),
                        answer: q.correct,
                        solution: q.explanation,
                        subject: q.subject || 'General',
                        topic: q.tags || 'Unknown Topic',
                        difficulty: getDifficultyFromLevel(q.level),
                        type: q.type || 'Single Choice',
                    }));
                    setQuestions(formattedQuestions);
                }
            } catch (err) {
                console.error('Error fetching chapter questions:', err);
                setError('Failed to load questions for this chapter. Please try again later.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchQuestions();
    }, [selectedChapter, activeSubject]);
    
    const difficultyFilteredQuestions = useMemo(() => {
        if (activeDifficulty === 'All') {
            return questions;
        }
        return questions.filter(q => q.difficulty === activeDifficulty);
    }, [questions, activeDifficulty]);

    const totalPages = Math.ceil(difficultyFilteredQuestions.length / PAGE_SIZE);
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const questionsForCurrentPage = difficultyFilteredQuestions.slice(startIndex, startIndex + PAGE_SIZE);

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
            document.querySelector('main')?.scrollTo(0, 0);
        }
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
            document.querySelector('main')?.scrollTo(0, 0);
        }
    };

    const handleChapterSelect = (chapterName: string) => {
        setSelectedChapter(chapterName);
        setActiveDifficulty('All');
        setCurrentPage(1);
        window.scrollTo(0, 0);
    };
    
    const handleDifficultyChange = (difficultyName: string) => {
        setActiveDifficulty(difficultyName);
        setCurrentPage(1);
    };

    const handleBackToChapters = () => {
        setSelectedChapter(null);
        setError(null);
        setQuestions([]);
        setActiveDifficulty('All');
        setCurrentPage(1);
    };

    const chapters = useMemo(() => {
        return activeSubject === 'Mathematics' ? mathematicsChapters : generalAbilityChapters;
    }, [activeSubject]);

    const SubjectButton = ({ subjectName }) => {
        const isActive = activeSubject === subjectName;
        return (
            <button
                onClick={() => setActiveSubject(subjectName)}
                className={`flex-1 py-3 px-4 text-center text-base font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary ${
                    isActive
                        ? 'border-b-2 border-brand-primary text-brand-primary'
                        : 'border-b-2 border-transparent text-brand-text-secondary hover:text-brand-text-primary hover:border-gray-300'
                }`}
            >
                {subjectName}
            </button>
        );
    };

    if (selectedChapter) {
        // FIX: Add explicit types for props to allow 'key' prop in map.
        const DifficultyButton: React.FC<{ difficultyName: string }> = ({ difficultyName }) => {
            const isActive = activeDifficulty === difficultyName;
            return (
                <button
                    onClick={() => handleDifficultyChange(difficultyName)}
                    className={`py-3 px-4 text-center text-sm font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary ${
                        isActive
                            ? 'border-b-2 border-brand-primary text-brand-primary'
                            : 'border-b-2 border-transparent text-brand-text-secondary hover:text-brand-text-primary hover:border-gray-300'
                    }`}
                >
                    {difficultyName}
                </button>
            );
        };

        return (
            <div className="animate-fade-in space-y-6">
                <header>
                    <button onClick={handleBackToChapters} className="flex items-center space-x-2 text-sm font-semibold text-brand-text-secondary hover:text-brand-primary transition-colors mb-4">
                        <ArrowLeftIcon className="w-5 h-5" />
                        <span>Back to Chapters</span>
                    </button>
                    <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-brand-text-primary">{selectedChapter}</h1>
                    <p className="mt-2 text-lg text-brand-text-secondary">
                        Practice questions for {activeSubject}.
                    </p>
                </header>

                <div className="sticky top-[63px] z-10 bg-brand-bg/90 backdrop-blur-sm -my-2 py-2">
                    <nav className="flex items-center border-b border-brand-border">
                        {['All', 'Easy', 'Medium', 'Hard'].map(difficulty => (
                            <DifficultyButton key={difficulty} difficultyName={difficulty} />
                        ))}
                    </nav>
                </div>

                {isLoading && (
                    <div className="flex-1 flex items-center justify-center min-h-[calc(100vh-400px)]">
                        <div className="flex flex-col items-center">
                            <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
                            <p className="mt-4 text-brand-text-secondary">Fetching questions...</p>
                        </div>
                    </div>
                )}
                
                {error && (
                    <div className="text-center py-16 bg-brand-surface rounded-lg border border-brand-border border-red-200">
                        <h3 className="text-lg font-semibold text-brand-accent-red">An Error Occurred</h3>
                        <p className="text-brand-text-secondary mt-1">{error}</p>
                    </div>
                )}
                
                {!isLoading && !error && (
                    difficultyFilteredQuestions.length > 0 ? (
                        <div className="space-y-6">
                            {questionsForCurrentPage.map(q => {
                                const isBookmarked = !!user?.bookmarks?.questions?.[q.id];
                                return <QuestionCard key={q.id} question={q} user={user} isBookmarked={isBookmarked} onToggleBookmark={onToggleBookmark} onNavigateToSignIn={onNavigateToSignIn} />;
                            })}

                            {totalPages > 1 && (
                                <div className="flex items-center justify-between mt-8 pt-4 border-t border-brand-border">
                                    <button
                                        onClick={handlePrevPage}
                                        disabled={currentPage === 1}
                                        className="px-4 py-2 text-sm font-semibold rounded-lg border border-brand-border bg-brand-surface hover:bg-gray-100 dark:hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Previous
                                    </button>
                                    <span className="text-sm font-semibold text-brand-text-secondary">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        onClick={handleNextPage}
                                        disabled={currentPage === totalPages}
                                        className="px-4 py-2 text-sm font-semibold rounded-lg border border-brand-border bg-brand-surface hover:bg-gray-100 dark:hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                         <div className="text-center py-16 bg-brand-surface rounded-lg border border-brand-border">
                            <h3 className="text-lg font-semibold text-brand-text-primary">No Questions Found</h3>
                            <p className="text-brand-text-secondary mt-1">
                                {questions.length > 0 
                                    ? `There are no "${activeDifficulty}" questions in this chapter. Try another difficulty.`
                                    : `We couldn't find any questions for this chapter yet. Please check back later.`
                                }
                            </p>
                        </div>
                    )
                )}
            </div>
        );
    }

    return (
        <div className="animate-fade-in space-y-6">
            <header>
                <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-brand-text-primary">NDA: Chapter Wise Practice</h1>
                <p className="mt-2 text-lg text-brand-text-secondary">
                    Select a subject to see available chapters for your practice session.
                </p>
            </header>

            <div className="sticky top-[63px] z-10 bg-brand-bg/90 backdrop-blur-sm -my-2 py-2">
                <nav className="flex items-center border-b border-brand-border">
                    <SubjectButton subjectName="Mathematics" />
                    <SubjectButton subjectName="General Ability" />
                </nav>
            </div>
            
            <div className="mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {chapters.map(chapter => (
                        <ChapterCard key={chapter.name} chapter={chapter} onClick={() => handleChapterSelect(chapter.name)} />
                    ))}
                </div>
            </div>
        </div>
    );
};